import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import axios from 'axios'
import { API_BASE_URL } from '../constants'
import toast from 'react-hot-toast'

// Create axios instance with base URL for auth
const authApi = axios.create({
  baseURL: API_BASE_URL
})

interface User {
  id: number
  walletAddress: string
  username?: string
  email?: string
  tokenBalance: number
  reputationScore: number
  stats?: {
    totalMarketsCreated: number
    successfulResolutions: number
    totalBets: number
    totalWagered: number
    totalWinnings: number
    profitLoss: number
  }
}

interface AuthState {
  user: User | null
  token: string | null
  isLoading: boolean
  isAuthenticated: boolean
  
  // Actions
  login: (walletAddress: string, signature?: string) => Promise<void>
  logout: () => void
  updateProfile: (data: { username?: string; email?: string }) => Promise<void>
  refreshUser: () => Promise<void>
  setLoading: (loading: boolean) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: true,
      isAuthenticated: false,

      login: async (walletAddress: string, signature?: string) => {
        try {
          set({ isLoading: true })
          
          console.log('🔐 Attempting login with wallet:', walletAddress.slice(0, 8) + '...')
          
          const response = await authApi.post('/api/users/auth', {
            walletAddress,
            signature
          })

          const { token, user } = response.data
          console.log('✅ Login successful, user:', user?.id)

          // Set axios default authorization header for all future requests
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
          authApi.defaults.headers.common['Authorization'] = `Bearer ${token}`

          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false
          })

          toast.success(`Welcome ${user.username || 'to CPX'}!`)
          
        } catch (error: any) {
          console.error('❌ Login error:', error)
          toast.error(error.response?.data?.error || 'Login failed')
          set({ isLoading: false })
        }
      },

      logout: () => {
        // Clear axios default authorization header
        delete axios.defaults.headers.common['Authorization']
        
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false
        })

        toast.success('Logged out successfully')
      },

      updateProfile: async (data: { username?: string; email?: string }) => {
        try {
          const response = await authApi.put('/api/users/me', data)
          
          set(state => ({
            user: state.user ? { ...state.user, ...response.data } : null
          }))

          toast.success('Profile updated successfully')
        } catch (error: any) {
          console.error('Profile update error:', error)
          toast.error(error.response?.data?.error || 'Profile update failed')
          throw error
        }
      },

      refreshUser: async () => {
        try {
          const { token } = get()
          if (!token) {
            set({ isLoading: false })
            return
          }

          // Set auth header before making the request
          authApi.defaults.headers.common['Authorization'] = `Bearer ${token}`
          
          const response = await authApi.get('/api/users/me')
          
          set({
            user: response.data,
            isAuthenticated: true,
            isLoading: false
          })
          
        } catch (error: any) {
          console.error('Refresh user error:', error)
          
          // If token is invalid, logout
          if (error.response?.status === 401) {
            get().logout()
          } else {
            set({ isLoading: false })
          }
        }
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading })
      }
    }),
    {
      name: 'auth-storage',
      onRehydrateStorage: () => (state) => {
        if (state?.token) {
          // Set axios default authorization header
          axios.defaults.headers.common['Authorization'] = `Bearer ${state.token}`
          // Refresh user data
          state.refreshUser()
        } else {
          state?.setLoading(false)
        }
      }
    }
  )
)
